import { IBlogPayload, IDeleteDataResponseAndPayload } from "../../../types/api/blog";
import { TResponse, TResponseWithQData } from "../../../types/common"
import { useMutation, UseMutationOptions, UseMutationResult, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { blogDetail, deleteBlog, listBlog, saveBlog } from "./callback";
import { QData } from "../../../types/basic-type-list";
import { API_ENDPOINTS } from "../../../constants/api_endpoints"
import { useState } from "react";

export const useSaveBlog = ({ qData = { page: 1, limit: 20 }, onSuccess, onError }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IBlogPayload>) => void;
    onError?: (error: unknown) => void;
  }
  ,
  options: UseMutationOptions<TResponse<IBlogPayload>, unknown, IBlogPayload> = {}
): UseMutationResult<TResponse<IBlogPayload>, unknown, IBlogPayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IBlogPayload>, unknown, IBlogPayload>({
    mutationFn: async (data: IBlogPayload) => {
      const response = await saveBlog(data);
      return response.data;
    },
    onSuccess: (res: any, ...rest) => {
      queryClient.setQueryData([API_ENDPOINTS.BLOG_LIST, { ...qData, total: null }], (old: any) => {
        if (!old) return;
        const exists = old.data.find((v: any) => v._id === res.data._id);
        return {
          ...old,
          data: exists  
            ? old.data.map((v: any) => (v._id === res.data._id ? res.data : v))
            : [res.data, ...old.data]
        };
      });

        queryClient.setQueryData(
        [API_ENDPOINTS.BLOG_DETAILS, res.data._id],
        {
          success: true,
          message: "",
          code: 200,
          data: res.data,
        }
      );

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options
  });
};

export const useListBlog = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<TResponseWithQData<IBlogPayload[]>, Error>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<TResponseWithQData<IBlogPayload[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IBlogPayload[]>, Error>({
    queryKey: [API_ENDPOINTS.BLOG_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await listBlog(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map(x => ({ key: x._id, ...x })),
      qData: { ...queryParams, total: res.extra?.total }
    }),
    initialData: {
      data: [],
      success: true,
      message: '',
      code: 200,
      qData: { page: 1, limit: 20 },
      extra: { page: 1, limit: 20, total: 0 },
    },
    staleTime: 300000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams((prev: any) => ({ ...prev, ...params }));
    },
  };
};

export const useDeleteBlog = (
  { qData = { page: 1, limit: 20 }, onSuccess, onError }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IDeleteDataResponseAndPayload>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IDeleteDataResponseAndPayload>,
    unknown,
    IDeleteDataResponseAndPayload
  > = {}
) => {

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: IDeleteDataResponseAndPayload) => {
      const response = await deleteBlog(data);
      return response.data;
    },

    onSuccess: (res, variables, ...rest) => {
      const deleteId = variables.ids;

      queryClient.setQueryData(
        [API_ENDPOINTS.BLOG_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return old;

          return {
            ...old,
            data: old.data.filter((item: any) => item._id !== deleteId) 
          };
        }
      );

      onSuccess?.(res);
      options?.onSuccess?.(res, variables, ...rest);
    },

    onError: (error, variables, ...rest) => {
      onError?.(error);
      options?.onError?.(error, variables, ...rest);
    },

    ...options
  });
};


export const useBlogDetails = (
  _id: string,
  options?: Omit<
    UseQueryOptions<TResponse<IBlogPayload>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<TResponse<IBlogPayload>, Error> => {
  return useQuery<TResponse<IBlogPayload>, Error>({
    queryKey: [API_ENDPOINTS.BLOG_DETAILS, _id],
    queryFn: async () => {
      const res = await blogDetail(_id);
      return res.data;
    },
    enabled: !!_id,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    ...options,
  });
};
